<script lang="ts">
  /**
   * A setting that is an amount: a volume, a speed. A row of a `Group` with
   * the label (and an optional badge) over a full-width slider, the current
   * value at the end of the label line. One real `<input type="range">`, so
   * the keyboard, a screen reader and a drag all work the way the platform
   * makes them; the accent paints its track.
   *
   * `bare` drops the row (no padding, no hairline, no label line) for a
   * slider that sits inside another row: the volume under a sound
   * `ToggleRow`'s switch, in its `children`. `label` is still its accessible
   * name.
   *
   * The parent owns `value`; `oninput` reports every move, `onchange` the
   * value when the drag ends (for saving).
   *
   * Test id: `testid`, on the input. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly label: string;
    readonly value: number;
    readonly min?: number;
    readonly max?: number;
    readonly step?: number;
    /** The value as words ("80 %"), shown and read out instead of the number. */
    readonly valueText?: string | undefined;
    readonly oninput: (value: number) => void;
    readonly onchange?: ((value: number) => void) | undefined;
    readonly disabled?: boolean;
    readonly badge?: Snippet | undefined;
    readonly bare?: boolean;
    readonly testid?: string | undefined;
  }
  const {
    label,
    value,
    min = 0,
    max = 1,
    step = 0.01,
    valueText,
    oninput,
    onchange,
    disabled = false,
    badge,
    bare = false,
    testid,
  }: Props = $props();

  function read(event: Event): number {
    return Number((event.currentTarget as HTMLInputElement).value);
  }
</script>

{#snippet slider()}
  <input
    class="ui-slider"
    type="range"
    {min}
    {max}
    {step}
    {value}
    {disabled}
    aria-label={label}
    aria-valuetext={valueText}
    data-testid={testid}
    oninput={(event) => oninput(read(event))}
    onchange={(event) => onchange?.(read(event))}
  />
{/snippet}

{#if bare}
  {@render slider()}
{:else}
  <div class="ui-slider-row">
    <span class="ui-slider-row-head">
      {#if badge !== undefined}{@render badge()}{/if}
      <span class="ui-slider-row-label" aria-hidden="true">{label}</span>
      {#if valueText !== undefined}
        <span class="ui-slider-row-value" aria-hidden="true">{valueText}</span>
      {/if}
    </span>
    {@render slider()}
  </div>
{/if}

<style>
  .ui-slider-row {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
    padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--ui-line);
    color: var(--ui-ink);
    font: var(--ui-text-body);
  }

  .ui-slider-row:last-child {
    border-bottom: 0;
  }

  .ui-slider-row-head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  .ui-slider-row-label {
    flex: 1 1 auto;
    min-width: 0;
  }

  .ui-slider-row-value {
    flex: 0 0 auto;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink-2);
  }

  .ui-slider {
    width: 100%;
    min-width: 0;
    height: 1.75rem;
    margin: 0;
    accent-color: var(--ui-accent);
  }

  .ui-slider:disabled {
    opacity: 0.5;
  }

  .ui-slider:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }
</style>
